import React, { useState, useEffect, useRef } from 'react';
import { Modal } from '../ui/modal';
import { Button } from '../ui/button';
import { convertKoreanToEnglish, cleanQRCodeData } from '../../lib/utils';

interface BarcodeScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onResult: (result: string) => void;
}

/**
 * USB 바코드 스캐너 컴포넌트
 * USB로 연결된 바코드 스캐너를 통해 QR코드를 스캔하고 결과를 반환합니다.
 * 바코드 스캐너는 키보드 입력 장치로 작동하여 스캔 결과를 텍스트로 입력합니다.
 */
const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ isOpen, onClose, onResult }) => {
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 모달이 열릴 때 입력 필드에 자동 포커스 및 영어 입력 모드 설정
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // 약간의 지연을 두고 포커스를 설정 (모달 애니메이션 완료 후)
      setTimeout(() => {
        inputRef.current?.focus();
        
        // 브라우저 입력 메서드 영어로 강제 설정 시도
        try {
          if (inputRef.current) {
            const input = inputRef.current;
            
            // 1. 기본 IME 비활성화
            (input.style as any).imeMode = 'disabled';
            input.setAttribute('data-ime-mode', 'disabled');
            
            // 2. 가상 키보드 API (최신 브라우저)
            if ('virtualKeyboard' in navigator) {
              try {
                (navigator as any).virtualKeyboard.policy = 'manual';
                console.log('🔤 [BarcodeScanner] 가상 키보드 API 설정 완료');
              } catch (vkError) {
                console.warn('⚠️ [BarcodeScanner] 가상 키보드 API 설정 실패:', vkError);
              }
            }
            
            // 3. Input Method Editor 상태 감지
            const detectIMEState = () => {
              const isComposing = (input as any).isComposing;
              if (isComposing) {
                console.warn('⚠️ [BarcodeScanner] IME 조합 모드 활성화됨');
                // IME 조합 상태에서는 경고 표시
                setError('한글 입력 모드가 감지되었습니다. 키보드를 영어 모드로 변경해주세요.');
              }
            };
            
            // 4. 다양한 이벤트로 IME 상태 감지
            input.addEventListener('compositionstart', detectIMEState);
            input.addEventListener('compositionupdate', detectIMEState);
            input.addEventListener('compositionend', () => {
              console.log('✅ [BarcodeScanner] IME 조합 모드 종료');
              if (error?.includes('한글 입력 모드')) {
                setError(null);
              }
            });
            
            console.log('🔤 [BarcodeScanner] 영어 입력 모드 설정 완료');
          }
        } catch (error) {
          console.warn('⚠️ [BarcodeScanner] 입력 모드 설정 실패:', error);
        }
      }, 100);
    }
  }, [isOpen]);

  // 입력값 실시간 정리 및 QR코드 스캔 감지
  useEffect(() => {
    if (inputValue && !error && !isProcessing) {
      // 한글이나 문제가 있는 문자가 포함된 경우 실시간 정리
      const hasKorean = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(inputValue);
      const hasDuplicates = /(.)\1{2,}/g.test(inputValue) || inputValue.includes('regss') || inputValue.includes('exhh');
      const hasMissingChars = inputValue.includes('regstration') || inputValue.includes('reistration') ||
                             inputValue.includes('registraton') || inputValue.includes('registation') ||
                             inputValue.includes('regisration');
      
      if (hasKorean || hasDuplicates || hasMissingChars) {
        console.log('🔧 [BarcodeScanner] 실시간 문제 감지, 정리 시작:', inputValue);
        
        const cleanedValue = cleanQRCodeData(inputValue);
        console.log('🔄 [BarcodeScanner] 실시간 정리 결과:', cleanedValue);
        
        if (cleanedValue !== inputValue) {
          setInputValue(cleanedValue);
          return; // 정리된 값으로 재진입하므로 여기서 종료
        }
      }
      
      const timer = setTimeout(() => {
        processQRCode();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [inputValue, error, isProcessing]);

  // QR코드 처리 함수
  const processQRCode = async () => {
    if (!inputValue || inputValue.trim() === '') {
      setError('QR코드 데이터가 비어있습니다.');
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);
      
      // 바코드 스캐너에서 추가될 수 있는 접두사/접미사 제거
      let cleanedQRCode = inputValue.trim();
      
      // 일반적인 바코드 스캐너 접두사 제거 (예: "QR:", "CODE:", 등)
      const prefixesToRemove = ['QR:', 'CODE:', 'BARCODE:', 'SCAN:'];
      for (const prefix of prefixesToRemove) {
        if (cleanedQRCode.startsWith(prefix)) {
          cleanedQRCode = cleanedQRCode.substring(prefix.length);
          break;
        }
      }
      
      // 제어 문자 제거
      // eslint-disable-next-line no-control-regex
      cleanedQRCode = cleanedQRCode.replace(/[\x00-\x1F\x7F]/g, '');
      
      // 최종 검증
      if (!cleanedQRCode || cleanedQRCode.length < 5) {
        setError('유효하지 않은 QR코드 형식입니다.');
        return;
      }
      
      console.log('처리할 QR코드:', cleanedQRCode);
      
      // 결과 콜백 호출
      onResult(cleanedQRCode);
      
      // 입력 필드 초기화
      setInputValue('');
      
    } catch (err) {
      console.error('QR코드 처리 오류:', err);
      setError('QR코드 처리 중 오류가 발생했습니다.');
    } finally {
      setIsProcessing(false);
    }
  };

  // 키보드 이벤트 처리 (바코드 스캐너 최적화 및 한글 입력 방지)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // 한글 입력 모드일 때 나타나는 키 조합 차단
    if (e.nativeEvent.isComposing || e.key === 'Process') {
      console.warn('⚠️ [BarcodeScanner] 한글 입력 모드 감지됨, 이벤트 차단');
      e.preventDefault();
      return;
    }
  };

  // 엔터키 처리 (바코드 스캐너는 보통 엔터키를 자동으로 입력)
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // 한글 조합 중인 경우 차단
    if (e.nativeEvent.isComposing) {
      e.preventDefault();
      return;
    }
    
    if (e.key === 'Enter') {
      e.preventDefault();
      processQRCode();
      return;
    }
    
    // 영어/숫자/특수문자만 허용
    const allowedChars = /^[a-zA-Z0-9\{\}\[\]"':,\-_\s]$/;
    if (!allowedChars.test(e.key)) {
      console.warn('⚠️ [BarcodeScanner] 허용되지 않은 문자 입력 시도:', e.key);
      // 차단하지 않고 로그만 남김 (한글 변환된 QR 데이터도 처리해야 하므로)
    }
  };

  // 수동 스캔 버튼 클릭
  const handleManualScan = () => {
    processQRCode();
  };

  // 모달 닫기
  const handleClose = () => {
    setInputValue('');
    setError(null);
    setIsProcessing(false);
    onClose();
  };

  // 입력값 변경 핸들러 (실시간 한글→영어 변환 및 중복 제거)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    
    // 한글 또는 문제가 있는 문자가 포함된 경우 실시간 정리
    const hasKorean = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(rawValue);
    const hasDuplicates = /(.)\1{2,}/g.test(rawValue) || rawValue.includes('regss') || rawValue.includes('exhh');
    const hasMissingChars = rawValue.includes('regstration') || rawValue.includes('reistration') ||
                           rawValue.includes('registraton') || rawValue.includes('registation') ||
                           rawValue.includes('regisration');
    
    if (hasKorean || hasDuplicates || hasMissingChars) {
      console.log('⚡ [BarcodeScanner] 입력 중 문제 감지, 즉시 정리:', rawValue);
      
      const cleanedValue = cleanQRCodeData(rawValue);
      console.log('⚡ [BarcodeScanner] 즉시 정리 결과:', cleanedValue);
      
      setInputValue(cleanedValue);
    } else {
      setInputValue(rawValue);
    }
  };

  // 입력 필드 클리어
  const handleClear = () => {
    setInputValue('');
    setError(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" title="바코드 스캔">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">바코드 스캔</h2>
          <Button variant="outline" onClick={handleClose}>
            닫기
          </Button>
        </div>

        {/* 사용법 안내 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="text-blue-600">📱</div>
            <div>
              <h3 className="font-medium text-blue-800">사용법</h3>
              <div className="text-sm text-blue-700 mt-1 space-y-1">
                <p><strong>⌨️ 중요: 키보드 모드를 영어로 변경하세요!</strong></p>
                <p>1. 윈도우: <kbd>Alt</kbd> + <kbd>Shift</kbd> 또는 <kbd>Ctrl</kbd> + <kbd>Space</kbd></p>
                <p>2. 맥: <kbd>⌘</kbd> + <kbd>Space</kbd>로 영어 입력 모드 변경</p>
                <p>3. 아래 입력 필드에 커서가 위치한 상태에서</p>
                <p>4. USB 바코드 스캐너로 QR코드를 스캔하세요</p>
                <p>5. 스캔이 완료되면 자동으로 처리됩니다</p>
              </div>
            </div>
          </div>
        </div>

        {/* 키보드 모드 경고 */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="text-yellow-600">⚠️</div>
            <div>
              <h3 className="font-medium text-yellow-800">키보드 입력 모드 확인</h3>
              <div className="text-sm text-yellow-700 mt-1">
                <p>QR 코드가 한글로 스캔되는 경우, 키보드 입력 모드가 한글로 설정되어 있습니다.</p>
                <p>반드시 <strong>영어 입력 모드</strong>로 변경한 후 스캔해주세요.</p>
                <p className="mt-2 font-medium text-green-700">✨ <strong>자동 변환 기능</strong>: 한글로 입력되어도 실시간으로 영어로 변환됩니다!</p>
              </div>
            </div>
          </div>
        </div>

        {/* 실시간 변환/교정 상태 표시 */}
        {inputValue && (/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(inputValue) || 
                       /(.)\1{2,}/g.test(inputValue) || 
                       inputValue.includes('regss') || 
                       inputValue.includes('exhh') ||
                       inputValue.includes('regstration') ||
                       inputValue.includes('reistration') ||
                       inputValue.includes('registraton') ||
                       inputValue.includes('registation') ||
                       inputValue.includes('regisration')) && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="text-green-600">🔄</div>
              <div>
                <h3 className="font-medium text-green-800">실시간 데이터 정리 중</h3>
                <div className="text-sm text-green-700 mt-1">
                  <p>
                    {/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(inputValue) && '한글이 감지되어 영어로 변환하고 있습니다. '}
                    {(/(.)\1{2,}/g.test(inputValue) || inputValue.includes('regss') || inputValue.includes('exhh')) && 
                     '중복 문자가 감지되어 교정하고 있습니다. '}
                    {(inputValue.includes('regstration') || inputValue.includes('reistration') ||
                      inputValue.includes('registraton') || inputValue.includes('registation') ||
                      inputValue.includes('regisration')) && 
                     '누락된 문자가 감지되어 교정하고 있습니다. '}
                  </p>
                  <p className="text-xs mt-1 font-mono bg-white p-2 rounded border">
                    현재 입력: {inputValue}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 에러 메시지 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2">
              <div className="text-red-600">❌</div>
              <div>
                <h3 className="font-medium text-red-800">오류 발생</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* QR코드 입력 필드 */}
        <div className="space-y-4">
          <div>
            <label htmlFor="qr-input" className="block text-sm font-medium text-gray-700 mb-2">
              QR코드 데이터
            </label>
            <input
              ref={inputRef}
              id="qr-input"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onKeyPress={handleKeyPress}
              placeholder="바코드 스캐너로 QR코드를 스캔하세요..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isProcessing}
              autoComplete="off"
              // 영어 입력 모드 강제 설정 속성들
              inputMode="text"
              spellCheck="false"
              autoCorrect="off"
              autoCapitalize="off"
              lang="en"
              style={{ 
                textTransform: 'none',
                unicodeBidi: 'bidi-override',
                direction: 'ltr'
              }}
              // 추가 데이터 속성들
              data-keyboard-mode="latin"
              data-ime-mode="disabled"
              data-input-method="latin"
              // 브라우저별 특수 속성들
              {...({
                'data-ms-ime-mode': 'disabled',
                'data-webkit-ime-mode': 'disabled',
                'data-moz-ime-mode': 'disabled'
              })}
              // HTML5 속성들
              translate="no"
              dir="ltr"
            />
          </div>

          {/* 버튼 그룹 */}
          <div className="flex gap-2">
            <Button
              onClick={handleManualScan}
              disabled={!inputValue || isProcessing}
              className="flex-1"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  처리 중...
                </>
              ) : (
                '수동 처리'
              )}
            </Button>
            <Button
              onClick={handleClear}
              variant="outline"
              disabled={isProcessing}
            >
              초기화
            </Button>
          </div>
        </div>

        {/* 추가 정보 */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">참고 사항</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• <strong>USB 바코드 스캐너가 연결되어 있는지 확인하세요</strong></li>
            <li>• <strong>시스템 키보드 모드를 영어로 설정하세요 (권장)</strong></li>
            <li>• 🔄 <strong>실시간 자동 변환</strong>: 한글로 입력되면 즉시 영어로 변환됩니다</li>
            <li>• 🔧 <strong>중복 문자 교정</strong>: 실시간 변환으로 인한 오타를 자동 수정합니다</li>
            <li>• 🔤 <strong>누락 문자 교정</strong>: 키보드 입력 오류로 인한 글자 누락을 자동 수정합니다</li>
            <li>• 📱 입력 필드는 자동으로 영어 모드 설정을 시도합니다</li>
            <li>• 🎯 IME 상태 감지: 한글 입력 모드 활성화 시 자동 경고</li>
            <li>• 🧹 접두사 제거: 바코드 스캐너의 접두사(QR:, CODE: 등) 자동 제거</li>
            <li>• ⚡ 연속 스캔: 각 스캔 후 입력 필드 자동 초기화</li>
            <li>• 🔧 <strong>완벽한 호환성</strong>: 어떤 입력 방식이든 정상 처리됩니다!</li>
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default BarcodeScanner; 